/**
 * PhotoViewModal.js
 * 
 * 檔案標頭註解：
 * 改版日期時間：2025-07-16 14:30:00
 * 版本：V1.01
 * 檔案主要功能與職責：照片檢視模態框，提供照片放大縮小、拖拽、下載、刪除等功能
 * 這個檔案是做什麼的？它解決了什麼問題？
 * - 提供完整的照片檢視介面
 * - 支援照片縮放和拖拽操作
 * - 顯示照片詳細資訊（拍攝者、時間、位置、市場等）
 * - 提供下載和刪除功能
 * - 支援地圖位置開啟
 * 
 * 版本更新說明：
 * V1.01 (2025-01-16) - 初始版本，從 App.js 模組化搬移
 * 
 * 注意事項：
 * - 需要傳入 config 物件包含 isOpen 和 photo 屬性
 * - 需要傳入 onClose 和 onDelete 回調函數
 * - 支援照片位置資訊顯示和地圖開啟
 * - 支援偵測到的市場資訊顯示
 * 
 * 關連檔案：
 * - App.js (主要使用此模組)
 * - 相關的 Firebase 資料庫操作
 */

import React, { useState } from 'react';

const PhotoViewModal = ({ config, onClose, onDelete }) => {
    const { isOpen, photo } = config || {};
    const [isZoomed, setIsZoomed] = useState(false);
    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    if (!isOpen) return null;

    // 【AI新增】照片旋轉處理函數
    // Why: 自動處理橫拍照片的顯示方向，確保照片正確顯示
    const getImageOrientation = (imageUrl) => {
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
                // 根據圖片尺寸判斷是否為橫拍
                const isLandscape = img.width > img.height;
                resolve(isLandscape ? 'landscape' : 'portrait');
            };
            img.src = imageUrl;
        });
    };

    // 【AI新增】放大縮小處理
    const handleZoom = (newScale) => {
        setScale(Math.max(0.5, Math.min(3, newScale)));
        if (newScale <= 1) {
            setPosition({ x: 0, y: 0 });
        }
    };

    // 【AI新增】拖拽處理
    const handleMouseDown = (e) => {
        if (scale > 1) {
            setIsDragging(true);
            setDragStart({
                x: e.clientX - position.x,
                y: e.clientY - position.y
            });
        }
    };

    const handleMouseMove = (e) => {
        if (isDragging && scale > 1) {
            setPosition({
                x: e.clientX - dragStart.x,
                y: e.clientY - dragStart.y
            });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    // 【AI新增】重置縮放
    const resetZoom = () => {
        setScale(1);
        setPosition({ x: 0, y: 0 });
    };

    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = photo.photoDataUrl;
        link.download = `photo_${photo.vendorId}_${photo.timestamp ? new Date(photo.timestamp).toISOString().split('T')[0] : Date.now()}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center p-4 z-[90]" onClick={onClose}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
                {/* 標題欄 */}
                <div className="flex justify-between items-center p-4 border-b flex-shrink-0">
                    <h3 className="text-xl font-bold text-gray-900">📸 照片詳情</h3>
                    <div className="flex items-center gap-2">
                        {/* 縮放控制 */}
                        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                            <button
                                onClick={() => handleZoom(scale - 0.5)}
                                className="px-2 py-1 text-sm bg-white rounded hover:bg-gray-50"
                                disabled={scale <= 0.5}
                            >
                                🔍-
                            </button>
                            <span className="text-sm px-2">{Math.round(scale * 100)}%</span>
                            <button
                                onClick={() => handleZoom(scale + 0.5)}
                                className="px-2 py-1 text-sm bg-white rounded hover:bg-gray-50"
                                disabled={scale >= 3}
                            >
                                🔍+
                            </button>
                            <button
                                onClick={resetZoom}
                                className="px-2 py-1 text-sm bg-white rounded hover:bg-gray-50"
                            >
                                🔄
                            </button>
                        </div>
                        <button 
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 text-2xl"
                        >
                            &times;
                        </button>
                    </div>
                </div>

                {/* 內容區域 */}
                <div className="flex-1 overflow-y-auto">
                    <div className="flex flex-col lg:flex-row h-full">
                        {/* 照片預覽區域 */}
                        <div className="flex-1 p-4 flex items-center justify-center bg-gray-100 min-h-[300px] lg:min-h-0">
                            <div 
                                className="relative overflow-hidden rounded-lg max-w-full max-h-full"
                                onMouseDown={handleMouseDown}
                                onMouseMove={handleMouseMove}
                                onMouseUp={handleMouseUp}
                                onMouseLeave={handleMouseUp}
                                style={{ cursor: scale > 1 ? 'grab' : 'default' }}
                            >
                                <img 
                                    src={photo.photoDataUrl} 
                                    alt="夥伴拍攝的照片"
                                    className="max-w-full max-h-full object-contain transition-transform duration-200"
                                    style={{
                                        transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
                                        transformOrigin: 'center'
                                    }}
                                    draggable={false}
                                />
                            </div>
                        </div>

                        {/* 照片資訊區域 */}
                        <div className="lg:w-80 flex-shrink-0">
                            <div className="p-4 space-y-4">
                                {/* 照片資訊 */}
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <h4 className="font-medium text-gray-800 mb-3">照片資訊</h4>
                                    <div className="space-y-3 text-sm">
                                        <div>
                                            <span className="text-gray-600">拍攝者：</span>
                                            <span className="font-medium">{photo.vendorName} ({photo.vendorId})</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">拍攝時間：</span>
                                            <span className="font-medium">
                                                {photo.timestamp ? new Date(photo.timestamp).toLocaleString('zh-TW') : '未知'}
                                            </span>
                                        </div>
                                        {/* 【AI新增】擺攤登記資訊 */}
                                        {photo.bookingInfo && (
                                            <>
                                                <div>
                                                    <span className="text-gray-600">擺攤日期：</span>
                                                    <span className="font-medium">{photo.bookingInfo.date}</span>
                                                </div>
                                                <div>
                                                    <span className="text-gray-600">市場名稱：</span>
                                                    <span className="font-medium">{photo.bookingInfo.marketName}</span>
                                                </div>
                                                <div>
                                                    <span className="text-gray-600">市場城市：</span>
                                                    <span className="font-medium">{photo.bookingInfo.marketCity}</span>
                                                </div>
                                            </>
                                        )}
                                        {/* 【AI新增】拍照位置資訊 */}
                                        {photo.location && (
                                            <>
                                                <div>
                                                    <span className="text-gray-600">拍照位置：</span>
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <span className="font-medium text-xs break-all">
                                                            {photo.location.latitude.toFixed(6)}, {photo.location.longitude.toFixed(6)}
                                                        </span>
                                                        {/* 【AI新增】開啟地圖按鈕 */}
                                                        <button
                                                            onClick={() => {
                                                                // 檢測設備類型，選擇合適的地圖應用
                                                                const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
                                                                const isAndroid = /Android/.test(navigator.userAgent);
                                                                
                                                                const lat = photo.location.latitude;
                                                                const lng = photo.location.longitude;
                                                                
                                                                let mapUrl;
                                                                if (isIOS) {
                                                                    // iOS 使用 Apple Maps
                                                                    mapUrl = `https://maps.apple.com/?q=${lat},${lng}`;
                                                                } else if (isAndroid) {
                                                                    // Android 使用 Google Maps
                                                                    mapUrl = `https://maps.google.com/?q=${lat},${lng}`;
                                                                } else {
                                                                    // 桌面版使用 Google Maps
                                                                    mapUrl = `https://maps.google.com/?q=${lat},${lng}`;
                                                                }
                                                                
                                                                window.open(mapUrl, '_blank');
                                                            }}
                                                            className="text-blue-600 hover:text-blue-800 text-xs bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded border border-blue-200 transition-colors"
                                                            title="在地圖中開啟此位置"
                                                        >
                                                            🗺️ 地圖
                                                        </button>
                                                    </div>
                                                </div>
                                                <div>
                                                    <span className="text-gray-600">精確度：</span>
                                                    <span className="font-medium">{photo.location.accuracy} 公尺</span>
                                                </div>
                                            </>
                                        )}
                                        {/* 【AI新增】偵測到的市場資訊 */}
                                        {photo.detectedMarket && (
                                            <>
                                                <div>
                                                    <span className="text-gray-600">偵測市場：</span>
                                                    <span className="font-medium text-blue-600">
                                                        {photo.detectedMarket.name} ({photo.detectedMarket.city})
                                                    </span>
                                                </div>
                                                <div>
                                                    <span className="text-gray-600">距離：</span>
                                                    <span className="font-medium text-green-600">
                                                        {(photo.detectedMarket.distance * 1000).toFixed(0)} 公尺
                                                    </span>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* 操作按鈕 */}
                                <div className="flex flex-col gap-2">
                                    <button
                                        onClick={handleDownload}
                                        className="w-full px-4 py-2 bg-green-500 hover:bg-green-600 text-white font-bold rounded-lg"
                                    >
                                        💾 下載照片
                                    </button>
                                    <button
                                        onClick={() => {
                                            onDelete(photo);
                                            onClose();
                                        }}
                                        className="w-full px-4 py-2 bg-red-500 hover:bg-red-600 text-white font-bold rounded-lg"
                                    >
                                        🗑️ 刪除照片
                                    </button>
                                    <button
                                        onClick={onClose}
                                        className="w-full px-4 py-2 bg-gray-200 text-gray-800 font-bold rounded-lg hover:bg-gray-300"
                                    >
                                        關閉
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PhotoViewModal; 